import {
  PORT_CALL_ACTIVITIES,
  PORT_CALL_LOCATION_TYPES,
  type PortCallActivity,
  type PortCallLocationType,
  portCallStatus,
} from '@felix/domain';
import {
  ActionIcon,
  Autocomplete,
  Button,
  Chip,
  FileButton,
  Select,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconPaperclip, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { offsetChoices } from '../../inputs/lt-utc.js';
import { PortPicker } from '../../inputs/PortPicker.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import { vesselTime } from '../personnel/ship-clock.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { useCountryName, usePortCatalog } from '../ports/catalog-context.js';
import type { VoyageRoute } from '../ports/types.js';
import { LoadError } from '../purchasing/table.js';
import { fromLocalInput, toLocalInput } from '../voyage/NoonSections.js';
import { formatTzOffset, TZ_OFFSETS } from '../voyage/noon-report.js';
import type { Voyage } from '../voyage/types.js';
import { DOCUMENT_ACCEPT, localStamp, PortCallInfo, PortCallSummary } from './PortCallParts.js';
import classes from './PortCalls.module.css';
import {
  agentSuggestions,
  formIssues,
  formToInput,
  newPortCallValues,
  type PortCallFormValues,
  valuesOf,
} from './port-calls.js';
import type { PortCallEditor, PortCallsSource } from './source.js';
import type { PortCall } from './types.js';

type Loaded = {
  vessel: PersonnelVessel;
  voyages: Voyage[];
  calls: PortCall[];
  current: PortCall | null;
};

const MAX_FILE = 25 * 1024 * 1024;

/**
 * Yeni ya da düzeltilen liman araması (ortağın "New Port Call" formu; ADR 0052 §4): sefer ve
 * güzergâh öğesi (öğe limanı doldurur), liman seçici, ülke, yerel ETA/ETD/ATA/ATD ve saat farkı,
 * kalış yeri, acente (aynı limandaki önceki acenteler önerilir), faaliyetler, not ve belgeler.
 * Sağda canlı "Port Call Summary". Yalnız gemide.
 */
export function PortCallForm({
  source,
  editor,
  callId,
  onDone,
  onCancel,
}: {
  source: PortCallsSource;
  editor: PortCallEditor;
  callId: string | null;
  onDone: (id: string, notice: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`portCalls.form.${key}`, options);
  const fieldLabel = (key: string) => t(`portCalls.fields.${key}`);
  const errorText = (code: string) =>
    t(`portCalls.errors.${code}`, { defaultValue: t('portCalls.errors.GENERIC') });
  const { catalog } = usePortCatalog();
  const countryName = useCountryName();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<PortCallFormValues | null>(null);
  const [route, setRoute] = useState<VoyageRoute | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [vessels, list, current] = await Promise.all([
        source.vessels(),
        source.list(),
        callId ? source.get(callId) : Promise.resolve(null),
      ]);
      const vessel = vessels[0];
      if (!vessel) throw Object.assign(new Error('NOT_FOUND'), { code: 'NOT_FOUND' });
      const voyages = source.voyages ? await source.voyages(vessel.id) : [];
      setLoaded({ vessel, voyages, calls: list.items, current });
      if (current) {
        setValues(valuesOf(current));
      } else {
        // Gemi saati kaydından, dakika hassasiyetinde (ADR 0065 §4, §8)
        const offset = zoneOffsetMinutes(vesselTime(vessel), Date.now());
        const active = voyages.find((v) => v.status === 'active') ?? null;
        setValues(newPortCallValues({ tzOffsetMin: offset, voyageId: active?.id ?? null }));
      }
    } catch (error) {
      setLoadError(errorCode(error));
    }
  }, [source, callId]);
  useEffect(() => {
    void load();
  }, [load]);

  const voyageId = values?.voyageId ?? null;
  useEffect(() => {
    setRoute(null);
    if (!voyageId || !source.route) return;
    let live = true;
    source
      .route(voyageId)
      .then((next) => live && setRoute(next))
      .catch(() => live && setRoute(null));
    return () => {
      live = false;
    };
  }, [source, voyageId]);

  const countries = useMemo(() => {
    const codes = new Set((catalog?.ports ?? []).map((port) => port.country));
    return [...codes]
      .map((code) => ({ value: code, label: `${countryName(code)} (${code})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog, countryName]);

  const suggestions = useMemo(
    () =>
      loaded && values
        ? agentSuggestions(
            loaded.calls.filter((c) => c.id !== callId),
            values.portCode,
            values.portName,
          )
        : [],
    [loaded, values, callId],
  );

  if (loadError) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k(callId ? 'editTitle' : 'newTitle')} crumb={k('newTitle')} />
        <Panel>
          <LoadError title={k('loadError')} code={loadError} onRetry={() => void load()} />
        </Panel>
      </div>
    );
  }
  if (!loaded || !values) {
    return (
      <div className={classes.screen} aria-busy="true">
        <PageHeader title={k(callId ? 'editTitle' : 'newTitle')} />
        <div className={classes.layout}>
          <Skeleton height={520} radius="md" />
          <Skeleton height={420} radius="md" />
        </div>
      </div>
    );
  }

  const set = (next: Partial<PortCallFormValues>) => {
    setValues((current) => (current ? { ...current, ...next } : current));
    setDirty(true);
  };
  const countryOf = (code: string | null) => (code ? catalog?.byCode.get(code)?.country : null);
  const error = (field: string) => (issues[field] ? errorText(issues[field]) : null);
  const voyageOptions = [
    { value: '', label: k('noVoyage') },
    ...loaded.voyages
      .filter((v) => v.status !== 'cancelled' || v.id === values.voyageId)
      .map((v) => ({ value: v.id, label: `${v.voyageNo} · ${v.fromPort} – ${v.toPort}` })),
  ];
  const stopOptions = [
    { value: '', label: k('extraCall') },
    ...(route?.stops ?? []).map((stop, index) => ({
      value: stop.key,
      label: `${k(`roles.${stop.role}`)}${stop.role === 'via' ? ` ${index}` : ''} · ${stop.portName}`,
    })),
  ];
  // Kayıtlı ama güzergâhtan çıkarılmış öğe seçili kalır ("Not in route")
  if (values.itineraryKey && !stopOptions.some((o) => o.value === values.itineraryKey)) {
    stopOptions.push({ value: values.itineraryKey, label: t('portCalls.detail.notInRoute') });
  }

  const timeField = (field: 'eta' | 'etd' | 'ata' | 'atd', required: boolean) => {
    const value = values[field];
    return (
      <DateTimeInput
        label={`${fieldLabel(field)} (LT)`}
        withAsterisk={required}
        value={value ? toLocalInput(value, values.tzOffsetMin) : ''}
        onChange={(local) =>
          set({ [field]: local ? fromLocalInput(local, values.tzOffsetMin) : null })
        }
        clearable={!required}
        description={value ? `= ${localStamp(value, 0)} UTC` : undefined}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        error={error(field)}
        classNames={{ placeholder: classes.placeholder ?? '' }}
      />
    );
  };

  const save = async () => {
    const input = formToInput(values);
    const found = formIssues(input);
    setIssues(found);
    if (Object.keys(found).length > 0) {
      setFailure(k('fixFields'));
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const saved = loaded.current
        ? await editor.update(loaded.current.id, loaded.current.rowVersion, input)
        : await editor.create(input);
      const failed: string[] = [];
      for (const file of files) {
        try {
          await editor.upload(saved.id, file);
        } catch {
          failed.push(file.name);
        }
      }
      setDirty(false);
      onDone(
        saved.id,
        failed.length > 0
          ? k('savedWithFailedFiles', { files: failed.join(', ') })
          : k(loaded.current ? 'updated' : 'created'),
      );
    } catch (error) {
      setFailure(errorText(errorCode(error)));
    } finally {
      setBusy(false);
    }
  };

  const summary = {
    vesselName: loaded.vessel.name,
    portName: values.portName,
    portCode: values.portCode,
    country: values.country,
    locationType: values.locationType,
    berth: values.berth || null,
    eta: values.eta,
    etd: values.etd,
    ata: values.ata,
    atd: values.atd,
    tzOffsetMin: values.tzOffsetMin,
    agentName: values.agentName || null,
    activities: values.activities,
    status: portCallStatus({
      ata: values.ata,
      atd: values.atd,
      cancelledAt: null,
    }),
  };

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k(callId ? 'editTitle' : 'newTitle')}
        crumb={k(callId ? 'editTitle' : 'newTitle')}
        description={k('description')}
      />
      {failure ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {failure}
        </p>
      ) : null}
      <div className={classes.layout}>
        <form
          className={classes.main}
          aria-label={k(callId ? 'editTitle' : 'newTitle')}
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Panel title={k('sections.port')}>
            <div className={classes.formGrid}>
              <TextInput
                label={fieldLabel('vessel')}
                value={loaded.vessel.name}
                readOnly
                withAsterisk
              />
              <Select
                label={fieldLabel('voyage')}
                data={voyageOptions}
                value={values.voyageId ?? ''}
                allowDeselect={false}
                error={error('voyageId')}
                onChange={(value) => set({ voyageId: value || null, itineraryKey: null })}
              />
              <Select
                label={fieldLabel('routeStop')}
                data={stopOptions}
                value={values.itineraryKey ?? ''}
                allowDeselect={false}
                disabled={!values.voyageId}
                error={error('itineraryKey')}
                onChange={(value) => {
                  const stop = route?.stops.find((s) => s.key === value);
                  if (!stop) {
                    set({ itineraryKey: null });
                    return;
                  }
                  set({
                    itineraryKey: stop.key,
                    portName: stop.portName,
                    portCode: stop.portCode,
                    country: countryOf(stop.portCode) ?? values.country,
                  });
                }}
              />
              <PortPicker
                label={fieldLabel('port')}
                withAsterisk
                value={{ name: values.portName, code: values.portCode }}
                error={error('portName') ?? error('portCode')}
                onChange={(next) =>
                  set({
                    portName: next.name,
                    portCode: next.code,
                    ...(next.code ? { country: countryOf(next.code) ?? values.country } : {}),
                  })
                }
              />
              <Select
                label={fieldLabel('country')}
                withAsterisk
                searchable
                data={countries}
                value={values.country || null}
                disabled={values.portCode !== null}
                error={error('country')}
                onChange={(value) => set({ country: value ?? '' })}
              />
              <Select
                label={fieldLabel('locationType')}
                withAsterisk
                data={PORT_CALL_LOCATION_TYPES.map((type) => ({
                  value: type,
                  label: t(`portCalls.locationTypes.${type}`),
                }))}
                value={values.locationType}
                allowDeselect={false}
                onChange={(value) => value && set({ locationType: value as PortCallLocationType })}
              />
              <TextInput
                className={classes.wide}
                label={fieldLabel('berth')}
                placeholder={k('berthPlaceholder')}
                maxLength={200}
                value={values.berth}
                error={error('berth')}
                onChange={(event) => set({ berth: event.currentTarget.value })}
              />
            </div>
          </Panel>

          <Panel title={k('sections.times')}>
            <div className={classes.formGrid}>
              <Select
                label={fieldLabel('timeZone')}
                withAsterisk
                data={offsetChoices(values.tzOffsetMin, TZ_OFFSETS).map((m) => ({
                  value: String(m),
                  label: formatTzOffset(m),
                }))}
                value={String(values.tzOffsetMin)}
                allowDeselect={false}
                onChange={(value) => value !== null && set({ tzOffsetMin: Number(value) })}
              />
              {timeField('eta', true)}
              {timeField('etd', true)}
              <p className={classes.note}>{k('actualHint')}</p>
              {timeField('ata', false)}
              {timeField('atd', false)}
            </div>
          </Panel>

          <Panel title={k('sections.agent')}>
            <div className={classes.formGrid}>
              <Autocomplete
                label={fieldLabel('agentName')}
                data={suggestions.map((s) => s.agentName)}
                maxLength={200}
                value={values.agentName}
                error={error('agentName')}
                description={suggestions.length > 0 ? k('agentHint') : undefined}
                onChange={(value) => set({ agentName: value })}
                onOptionSubmit={(value) => {
                  const found = suggestions.find((s) => s.agentName === value);
                  if (found) {
                    set({
                      agentName: found.agentName,
                      agentContact: found.agentContact ?? '',
                      agentPhone: found.agentPhone ?? '',
                      agentEmail: found.agentEmail ?? '',
                    });
                  }
                }}
              />
              <TextInput
                label={fieldLabel('agentContact')}
                maxLength={200}
                value={values.agentContact}
                onChange={(event) => set({ agentContact: event.currentTarget.value })}
              />
              <TextInput
                label={fieldLabel('agentPhone')}
                maxLength={60}
                value={values.agentPhone}
                onChange={(event) => set({ agentPhone: event.currentTarget.value })}
              />
              <TextInput
                label={fieldLabel('agentEmail')}
                type="email"
                maxLength={200}
                value={values.agentEmail}
                error={error('agentEmail')}
                onChange={(event) => set({ agentEmail: event.currentTarget.value })}
              />
            </div>
          </Panel>

          <Panel title={k('sections.activities')}>
            <div className={classes.panelBody}>
              <Chip.Group
                multiple
                value={values.activities}
                onChange={(next) => set({ activities: next as PortCallActivity[] })}
              >
                <fieldset className={classes.chips} aria-label={k('sections.activities')}>
                  {PORT_CALL_ACTIVITIES.map((activity) => (
                    <Chip key={activity} value={activity} size="sm">
                      {t(`portCalls.activities.${activity}`)}
                    </Chip>
                  ))}
                </fieldset>
              </Chip.Group>
            </div>
          </Panel>

          <Panel title={k('sections.notes')}>
            <div className={classes.panelBody}>
              <Textarea
                aria-label={k('sections.notes')}
                placeholder={k('notesPlaceholder')}
                autosize
                minRows={3}
                maxLength={2000}
                value={values.notes}
                error={error('notes')}
                onChange={(event) => set({ notes: event.currentTarget.value })}
              />
            </div>
          </Panel>

          <Panel
            title={k('sections.documents')}
            extra={
              <FileButton
                multiple
                accept={DOCUMENT_ACCEPT}
                onChange={(picked) => {
                  const ok = picked.filter((file) => file.size <= MAX_FILE);
                  if (ok.length < picked.length) setFailure(errorText('FILE_TOO_LARGE'));
                  setFiles((current) => [...current, ...ok]);
                  setDirty(true);
                }}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    leftSection={<IconPaperclip size={14} />}
                  >
                    {k('addFiles')}
                  </Button>
                )}
              </FileButton>
            }
          >
            <div className={classes.panelBody}>
              <p className={classes.note}>{k('documentsHint')}</p>
              {files.length > 0 ? (
                <ul className={classes.pending}>
                  {files.map((file, index) => (
                    <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                      <span>{file.name}</span>
                      <ActionIcon
                        variant="subtle"
                        aria-label={k('removeFile', { name: file.name })}
                        onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Panel>

          <Panel flush>
            <div className={classes.footer}>
              <Button variant="default" onClick={onCancel} disabled={busy}>
                {t('portCalls.actions.back')}
              </Button>
              <Button type="submit" loading={busy}>
                {t('portCalls.actions.save')}
              </Button>
            </div>
          </Panel>
        </form>
        <aside className={classes.side} aria-label={k('preview')}>
          <PortCallSummary data={summary} />
          <PortCallInfo ship />
        </aside>
      </div>
    </div>
  );
}
